import { Link } from "react-router-dom";
import { Trash2, ArrowRight, ShoppingCart } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatGBP } from "../lib/api";

const Cart = () => {
  const { items, updateQuantity, removeItem, subtotal, clearCart } = useCart();

  if (items.length === 0)
    return (
      <div data-testid="cart-empty" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <ShoppingCart className="w-14 h-14 text-[#2C8C9C]/40 mx-auto" strokeWidth={1.25} />
        <h1 className="mt-6 text-3xl font-medium text-[#1B3A5C]" style={{ fontFamily: "Outfit" }}>
          Your cart is empty
        </h1>
        <p className="mt-3 text-[#64748B]">Browse the catalogue to add research peptides.</p>
        <Link
          to="/products"
          className="mt-8 inline-flex items-center gap-2 bg-[#1B3A5C] text-white px-7 py-3 text-sm font-medium hover:bg-[#254D7A] transition-colors"
        >
          View catalogue <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );

  return (
    <div data-testid="cart-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="sb-mono-label">Shopping cart</div>
      <h1 className="mt-2 text-4xl font-semibold text-[#1B3A5C] tracking-tight" style={{ fontFamily: "Outfit" }}>
        Your selection
      </h1>

      <div className="mt-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 border border-[#E2E8F0]">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr>
                <th className="text-left py-3 px-4 sb-mono-label !text-[#1B3A5C]">Item</th>
                <th className="text-center py-3 px-4 sb-mono-label !text-[#1B3A5C]">Qty</th>
                <th className="text-right py-3 px-4 sb-mono-label !text-[#1B3A5C]">Price</th>
                <th className="text-right py-3 px-4 sb-mono-label !text-[#1B3A5C]">Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.product_id} className="border-b border-[#E2E8F0] last:border-b-0">
                  <td className="py-4 px-4">
                    <div className="font-medium text-[#0F172A]">{it.name}</div>
                    <div className="font-mono text-xs text-[#64748B] mt-0.5">{it.sku} · {it.dosage}</div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <input
                      data-testid={`cart-qty-${it.sku}`}
                      type="number"
                      min={1}
                      max={it.stock}
                      value={it.quantity}
                      onChange={(e) => updateQuantity(it.product_id, Number(e.target.value) || 1)}
                      className="w-16 text-center border border-[#E2E8F0] py-1 font-mono"
                    />
                  </td>
                  <td className="py-4 px-4 text-right font-mono">{formatGBP(it.unit_price_gbp)}</td>
                  <td className="py-4 px-4 text-right font-mono font-medium">{formatGBP(it.unit_price_gbp * it.quantity)}</td>
                  <td className="py-4 px-4 text-right">
                    <button
                      data-testid={`cart-remove-${it.sku}`}
                      onClick={() => removeItem(it.product_id)}
                      className="text-[#64748B] hover:text-[#B91C1C]"
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 flex items-center justify-between bg-[#F8FAFC] border-t border-[#E2E8F0]">
            <button
              data-testid="cart-clear"
              onClick={clearCart}
              className="text-sm text-[#64748B] hover:text-[#B91C1C]"
            >
              Clear cart
            </button>
            <Link to="/products" className="text-sm text-[#2C8C9C] sb-link-underline" data-testid="cart-continue">
              ← Continue browsing
            </Link>
          </div>
        </div>

        <aside className="border border-[#E2E8F0] p-6 h-fit bg-[#F8FAFC]">
          <div className="sb-mono-label">Order summary</div>
          <div className="mt-4 flex items-baseline justify-between border-b border-[#E2E8F0] pb-4">
            <span className="text-sm text-[#475569]">Subtotal</span>
            <span data-testid="cart-subtotal" className="font-mono font-medium text-[#0F172A]">{formatGBP(subtotal)}</span>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <span className="text-sm text-[#475569]">Shipping</span>
            <span data-testid="cart-shipping" className="font-mono text-sm font-medium text-[#047857] uppercase">FREE</span>
          </div>
          <div className="mt-6 pt-6 border-t border-[#E2E8F0] flex items-baseline justify-between">
            <span className="text-sm font-medium text-[#1B3A5C]">Total</span>
            <span className="font-mono text-xl font-semibold text-[#1B3A5C]">{formatGBP(subtotal)}</span>
          </div>
          <Link
            to="/checkout"
            data-testid="cart-checkout-btn"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#1B3A5C] text-white px-6 py-3.5 text-sm font-medium tracking-wide hover:bg-[#254D7A] transition-colors"
          >
            Proceed to checkout <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-4 text-xs text-[#64748B] leading-relaxed">
            Guest checkout — no account required. Payment by UK bank transfer.
          </p>
        </aside>
      </div>
    </div>
  );
};

export default Cart;
